import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Grid,
  Stepper,
  Step,
  StepLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SendIcon from "@material-ui/icons/Send";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import CommonButton from "../../components/button"
import theme from "../../theme";

// ─── Styles ───────────────────────────────────────────────────────────────────
const useStyles = makeStyles(() => ({
  root: {
    background: theme.colors.White,
    borderRadius: 16,
    boxShadow: "0 2px 16px rgba(62,131,251,0.07), 0 1px 3px rgba(21,14,74,0.06)",
    padding: "28px 32px",
    width: "100%",
    boxSizing: "border-box",
    minHeight: 520,
    display: "flex",
    flexDirection: "column",
  },
  header: { marginBottom: 20 },
  title: {
    fontSize: 17,
    fontFamily: theme.fontFamily.MARKPROBLACK,
    color: theme.colors.Violet,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 13,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    marginTop: 4,
  },
  stepper: {
    padding: "0 0 24px 0",
    background: "transparent",
    "& .MuiStepIcon-root.MuiStepIcon-active": { color: theme.colors.DodgerBlue },
    "& .MuiStepIcon-root.MuiStepIcon-completed": { color: "#78F49E" },
  },
  sectionLabel: {
    fontSize: 10.5,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: theme.colors.DodgerBlue,
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    gap: 8,
    "&::after": { content: '""', flex: 1, height: 1, background: "#ECF3FF" },
  },
  fieldLabel: {
    fontSize: 11,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: "#8892a4",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    display: "block",
    marginBottom: 6,
  },
  fieldInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 13.5,
      fontFamily: theme.fontFamily.MARKPROBOOK,
      backgroundColor: "#FAFCFF",
      "& fieldset": { borderColor: theme.colors.Gallery, borderWidth: 1.5 },
      "&:hover fieldset": { borderColor: theme.colors.Periwinkle },
      "&.Mui-focused fieldset": { borderColor: theme.colors.DodgerBlue, borderWidth: 1.5 },
    },
  },
  helperError: {
    color: theme.colors.Monza,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12,
    marginTop: 4,
  },
  footer: {
    marginTop: "auto",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    borderTop: `1px solid #ECF3FF`,
    paddingTop: 20,
  },
  btnSecondary: {
    background: theme.colors.Porcelain,
    color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 13,
    textTransform: "none",
    borderRadius: 10,
    padding: "9px 20px",
    border: `1.5px solid ${theme.colors.Gallery}`,
  },
  btnPrimary: {
    background: theme.colors.DodgerBlue,
    color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 13,
    textTransform: "none",
    borderRadius: 10,
    padding: "9px 22px",
    boxShadow: "0 2px 10px rgba(62,131,251,0.25)",
    "&:hover": { background: "#2A6FE6" },
  },
}));

// ─── Validation ───────────────────────────────────────────────────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-()]{7,15}$/;
const NIN_BVN_REGEX = /^\d{11}$/;

const validateStep = (step, form) => {
  const e = {};

  if (step === 0) {
    if (!form.first_name?.trim()) e.first_name = "First name required";
    if (!form.last_name?.trim()) e.last_name = "Last name required";
    if (!form.email?.trim()) e.email = "Email required";
    else if (!EMAIL_REGEX.test(form.email)) e.email = "Invalid email";
    if (!form.gender) e.gender = "Select gender";
  }

  else if (step === 1) {
    if (!form.profile?.position?.trim()) e.position = "Position required";
    if (!form.profile?.phone?.trim()) e.phone = "Phone required";
    else if (!PHONE_REGEX.test(form.profile.phone)) e.phone = "Invalid phone format";
    if (!form.profile?.salary) e.salary = "Salary required";
  }

  else if (step === 2) {
    // NIN and BVN NOT REQUIRED - Only validate format if user actually types something
    if (form.profile?.nin && !NIN_BVN_REGEX.test(form.profile.nin)) e.nin = "Must be 11 digits";
    if (form.profile?.bvn && !NIN_BVN_REGEX.test(form.profile.bvn)) e.bvn = "Must be 11 digits";
  }

  else if (step === 4) {
    if (!form.password) e.password = "Password required";
    else if (form.password.length < 6) e.password = "Minimum 6 characters";
    if (form.password !== form.confirm_password) e.confirm_password = "Passwords do not match";
  }

  return e;
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, error, children, optional }) {
  const classes = useStyles();
  return (
    <Box>
      <span className={classes.fieldLabel}>
        {label} {optional && <span style={{ textTransform: 'none', opacity: 0.6 }}>(Optional)</span>}
      </span>
      {children}
      {error && <Typography className={classes.helperError}>{error}</Typography>}
    </Box>
  );
}

export default function AdminOnboardForm({
  form = {},
  setForm = () => { },
  onConfirmRegistration = () => { },
  onCancel,
  bankAccountComponent = null,
  title = "Admin Onboarding",
  loading = false,
  error = false,
}) {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});

  const steps = ["Personal", "Work", "Identity", "Bank", "Security"];

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const setProfileField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      profile: { ...(prev.profile ?? {}), [key]: value },
    }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleNext = () => {
    const stepErrors = validateStep(activeStep, form);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    if (activeStep === steps.length - 1) {
      onConfirmRegistration(form);
    } else {
      setActiveStep((prev) => prev + 1);
      setErrors({});
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const inputProps = (value, onChange, error) => ({
    fullWidth: true,
    variant: "outlined",
    size: "small",
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    error: !!error,
    className: classes.fieldInput,
    disabled: loading || error
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.subtitle}>
          Progress: Step {activeStep + 1} of {steps.length}
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} className={classes.stepper} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box py={2} flex={1}>
        {/* Step 0: Personal */}
        {activeStep === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field label="First Name" error={errors.first_name}>
                <TextField {...inputProps(form.first_name, (v) => setField("first_name", v), errors.first_name)} placeholder="Amara" />
              </Field>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field label="Last Name" error={errors.last_name}>
                <TextField {...inputProps(form.last_name, (v) => setField("last_name", v), errors.last_name)} placeholder="Okafor" />
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field label="Email Address" error={errors.email}>
                <TextField {...inputProps(form.email, (v) => setField("email", v), errors.email)} disabled />
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field label="Gender" error={errors.gender}>
                <TextField {...inputProps(form.gender, (v) => setField("gender", v), errors.gender)} select>
                  <MenuItem value="M">Male</MenuItem>
                  <MenuItem value="F">Female</MenuItem>
                </TextField>
              </Field>
            </Grid>
          </Grid>
        )}

        {/* Step 1: Work */}
        {activeStep === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field label="Position" error={errors.position}>
                <TextField {...inputProps(form.profile?.position, (v) => setProfileField("position", v), errors.position)} placeholder="HR Manager" />
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field label="Phone Number" error={errors.phone}>
                <TextField {...inputProps(form.profile?.phone, (v) => setProfileField("phone", v), errors.phone)} placeholder="+234..." />
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field label="Monthly Salary" error={errors.salary}>
                <TextField {...inputProps(form.profile?.salary, (v) => setProfileField("salary", v), errors.salary)} placeholder="e.g. 500000" />
              </Field>
            </Grid>
          </Grid>
        )}

        {/* Step 2: Identity (NOT REQUIRED) */}
        {activeStep === 2 && (
          <Box>
            <Typography className={classes.subtitle} style={{ marginBottom: 20 }}>
              These identification details are optional and can be filled later.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Field label="NIN" error={errors.nin} optional>
                  <TextField {...inputProps(form.profile?.nin, (v) => setProfileField("nin", v), errors.nin)} placeholder="11-digit Number" inputProps={{ maxLength: 11 }} />
                </Field>
              </Grid>
              <Grid item xs={12}>
                <Field label="BVN" error={errors.bvn} optional>
                  <TextField {...inputProps(form.profile?.bvn, (v) => setProfileField("bvn", v), errors.bvn)} placeholder="11-digit Number" inputProps={{ maxLength: 11 }} />
                </Field>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Step 3: Bank */}
        {activeStep === 3 && (
          <Box>
            <Typography className={classes.sectionLabel}>Bank Details</Typography>
            {bankAccountComponent}
          </Box>
        )}

        {/* Step 4: Security (Password) */}
        {activeStep === 4 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field label="Create Password" error={errors.password}>
                <TextField
                  {...inputProps(form.password, (v) => setField("password", v), errors.password)}
                  type="password"
                  placeholder="At least 6 characters"
                />
              </Field>
            </Grid>
            <Grid item xs={12}>
              <Field label="Confirm Password" error={errors.confirm_password}>
                <TextField
                  {...inputProps(form.confirm_password, (v) => setField("confirm_password", v), errors.confirm_password)}
                  type="password"
                  placeholder="Repeat password"
                />
              </Field>
            </Grid>
          </Grid>
        )}
      </Box>

      <Box className={classes.footer}>
        <Button
          onClick={activeStep === 0 ? onCancel : handleBack}
          className={classes.btnSecondary}
          startIcon={activeStep !== 0 && <ChevronLeftIcon />}
        >
          {activeStep === 0 ? "Cancel" : "Back"}
        </Button>

        <CommonButton
          className={classes.btnPrimary}
          endIcon={activeStep === steps.length - 1 ? <SendIcon /> : <ChevronRightIcon />}
          onClick={handleNext}
          text={activeStep === steps.length - 1 ? "Complete Registration" : "Next Step"}
          loading={loading}
          disabled={error}
        />
      </Box>
    </Box>
  );
}